import { watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'

const IDLE_TIMEOUT_MS = 60 * 60 * 1000 // 1 hour
const ACTIVITY_TS_KEY = 'last_activity_ts'

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
  'click',
] as const

/**
 * Composable that auto-signs out the user after 1 hour of inactivity.
 *
 * - Listens for user activity events and resets the idle timer on each one.
 * - Persists the last-activity timestamp in localStorage so that a page
 *   reload after a long idle period immediately triggers sign-out instead
 *   of resetting the clock.
 * - Only active while the user is authenticated; cleans up when they log out
 *   or the component is unmounted.
 */
export function useIdleTimeout() {
  const authStore = useAuthStore()
  const router = useRouter()

  let idleTimer: ReturnType<typeof setTimeout> | null = null

  function recordActivity() {
    localStorage.setItem(ACTIVITY_TS_KEY, Date.now().toString())

    if (idleTimer !== null) {
      clearTimeout(idleTimer)
    }

    idleTimer = setTimeout(handleIdleTimeout, IDLE_TIMEOUT_MS)
  }

  async function handleIdleTimeout() {
    console.log('[IdleTimeout] Session expired due to 1 hour of inactivity — signing out')
    stopTracking()
    await authStore.logout()
    router.push({ name: 'Auth', query: { reason: 'session_expired' } })
  }

  function startTracking() {
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, recordActivity, { passive: true })
    })
    recordActivity()
  }

  function stopTracking() {
    ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, recordActivity)
    })

    if (idleTimer !== null) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
  }

  // Activate/deactivate based on auth state
  watch(
    () => authStore.isLoggedIn,
    (isLoggedIn) => {
      if (isLoggedIn) {
        // Check if the session already expired before this page load
        const lastActivity = localStorage.getItem(ACTIVITY_TS_KEY)
        if (lastActivity) {
          const elapsed = Date.now() - parseInt(lastActivity, 10)
          if (elapsed >= IDLE_TIMEOUT_MS) {
            console.log('[IdleTimeout] Stale session detected on load — signing out')
            handleIdleTimeout()
            return
          }
          // Resume the timer with the remaining time
          const remaining = IDLE_TIMEOUT_MS - elapsed
          if (idleTimer !== null) clearTimeout(idleTimer)
          idleTimer = setTimeout(handleIdleTimeout, remaining)
        }

        startTracking()
      } else {
        stopTracking()
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    stopTracking()
  })
}
